import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api, type ToolEntry, type ToolsResponse } from "../lib/api.ts";

/**
 * Tools directory — granular catalog of every callable tool across the
 * platform. Skills are the containers (MCP servers, plugins); Tools are the
 * individual functions inside them, plus Claude Code built-ins and the
 * manager-agent chat orchestrator tools.
 *
 * Click any card → /tools/:fullKey to see description + per-agent allow/deny
 * matrix (for tools that can be gated per-agent — orchestrator tools are
 * info-only).
 */
export function Tools() {
	const [resp, setResp] = useState<ToolsResponse | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		api
			.listTools()
			.then(setResp)
			.catch((err) => setError((err as Error).message));
	}, []);

	const grouped = useMemo(() => {
		const mcpBySkill = new Map<string, ToolEntry[]>();
		const builtin: ToolEntry[] = [];
		const orchestrator: ToolEntry[] = [];
		const plugin: ToolEntry[] = [];
		for (const t of resp?.tools ?? []) {
			if (t.source.kind === "mcp") {
				const arr = mcpBySkill.get(t.source.skillKey) ?? [];
				arr.push(t);
				mcpBySkill.set(t.source.skillKey, arr);
			} else if (t.source.kind === "builtin") {
				builtin.push(t);
			} else if (t.source.kind === "orchestrator") {
				orchestrator.push(t);
			} else {
				plugin.push(t);
			}
		}
		const byName = (a: ToolEntry, b: ToolEntry) => a.name.localeCompare(b.name);
		for (const [k, arr] of mcpBySkill) {
			arr.sort(byName);
			mcpBySkill.set(k, arr);
		}
		builtin.sort(byName);
		orchestrator.sort(byName);
		plugin.sort(byName);
		return { mcpBySkill, builtin, orchestrator, plugin };
	}, [resp]);

	const totals = useMemo(() => {
		if (!resp) return null;
		const mcp = resp.tools.filter((t) => t.source.kind === "mcp").length;
		return {
			mcp,
			builtin: grouped.builtin.length,
			orchestrator: grouped.orchestrator.length,
			plugin: grouped.plugin.length,
			total: resp.tools.length,
		};
	}, [resp, grouped.builtin.length, grouped.orchestrator.length, grouped.plugin.length]);

	return (
		<div className="px-6 py-8 max-w-4xl mx-auto">
			<div className="mb-6">
				<h1 className="text-xl font-semibold">Tools</h1>
				<p className="text-sm text-zinc-500 mt-1">
					{resp === null
						? "Loading…"
						: `${totals?.mcp ?? 0} MCP · ${totals?.builtin ?? 0} built-in · ${totals?.orchestrator ?? 0} orchestrator · ${totals?.plugin ?? 0} plugin · ${totals?.total ?? 0} total`}
				</p>
			</div>

			{error && (
				<div className="p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300 mb-4">
					{error}
				</div>
			)}

			{resp?.errors && resp.errors.length > 0 && (
				<div className="p-3 border border-amber-500/30 rounded bg-amber-500/10 text-xs text-amber-300 mb-4 space-y-1">
					<div className="font-medium">Some MCP servers couldn't be listed:</div>
					{resp.errors.map((e) => (
						<div key={e.skillKey} className="font-mono">
							{e.skillKey}: {e.message}
						</div>
					))}
				</div>
			)}

			{[...grouped.mcpBySkill.entries()].map(([skillKey, tools]) => (
				<section key={skillKey} className="mb-8">
					<h2 className="text-sm font-medium text-zinc-400 mb-3">
						MCP ·{" "}
						<Link
							to={`/skills/${encodeURIComponent(skillKey)}`}
							className="font-mono text-zinc-300 hover:text-zinc-100 underline-offset-2 hover:underline"
						>
							{skillKey}
						</Link>
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{tools.map((t) => (
							<ToolCard key={t.fullKey} t={t} />
						))}
					</div>
				</section>
			))}

			{grouped.builtin.length > 0 && (
				<section className="mb-8">
					<h2 className="text-sm font-medium text-zinc-400 mb-3">Built-in Claude Code</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{grouped.builtin.map((t) => (
							<ToolCard key={t.fullKey} t={t} />
						))}
					</div>
				</section>
			)}

			{grouped.orchestrator.length > 0 && (
				<section className="mb-8">
					<h2 className="text-sm font-medium text-zinc-400 mb-3">
						Manager-agent orchestrator{" "}
						<span className="text-[11px] text-zinc-500 font-normal">(used by the owner chat — not gateable per project-agent)</span>
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{grouped.orchestrator.map((t) => (
							<ToolCard key={t.fullKey} t={t} />
						))}
					</div>
				</section>
			)}

			{grouped.plugin.length > 0 && (
				<section className="mb-8">
					<h2 className="text-sm font-medium text-zinc-400 mb-3">
						Plugin / marketplace / mcp-stdio{" "}
						<span className="text-[11px] text-zinc-500 font-normal">(tool inventory not enumerable — see skill)</span>
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{grouped.plugin.map((t) => (
							<ToolCard key={t.fullKey} t={t} />
						))}
					</div>
				</section>
			)}

			{resp !== null && resp.tools.length === 0 && (
				<div className="text-sm text-zinc-500 italic">No tools available.</div>
			)}
		</div>
	);
}

function ToolCard({ t }: { t: ToolEntry }) {
	const totalAgents = t.agents.length;
	const grantedActive = t.agents.filter((a) => a.granted && !a.denied).length;
	const usage =
		t.source.kind === "orchestrator"
			? "info-only"
			: totalAgents === 0
				? "no agents"
				: `${grantedActive}/${totalAgents} agents`;
	return (
		<Link
			to={`/tools/${encodeURIComponent(t.fullKey)}`}
			className="border border-zinc-800 rounded-lg bg-zinc-900/40 p-4 flex flex-col gap-2 hover:border-zinc-700 hover:bg-zinc-900/60 transition-colors"
		>
			<div className="flex items-baseline justify-between gap-3">
				<div className="min-w-0">
					<div className="text-sm font-semibold text-zinc-100 font-mono">{t.name}</div>
					{t.source.kind === "mcp" && (
						<div className="text-[11px] text-zinc-500">{t.source.serverDisplayName}</div>
					)}
				</div>
				<SourceBadge source={t.source} />
			</div>
			{t.description && <div className="text-xs text-zinc-400 line-clamp-2">{t.description}</div>}
			<div className="text-[11px] text-zinc-500 mt-1">{usage}</div>
		</Link>
	);
}

function SourceBadge({ source }: { source: ToolEntry["source"] }) {
	const map: Record<
		ToolEntry["source"]["kind"],
		{ label: string; cls: string }
	> = {
		mcp: { label: "MCP", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
		builtin: { label: "Built-in", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
		orchestrator: {
			label: "Orchestrator",
			cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
		},
		plugin: { label: "Plugin", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
	};
	const { label, cls } = map[source.kind];
	return (
		<span className={`text-[10px] px-2 py-0.5 rounded border ${cls} whitespace-nowrap`}>
			{label}
		</span>
	);
}
